/**
 * Reads the state of a query someone else owns, without becoming one of its
 * observers (W4, B2-liste).
 *
 * The contact list needs the loading and error states of the engine's queue
 * read (`DialerDataContext` Q1) to show a skeleton and an error line, but
 * must not change that read: a second `useQuery` on the same key would merge
 * its options into the query (queryFn, interval). This hook only subscribes to
 * the cache and returns the query's state object, which react-query replaces
 * on every change (a stable snapshot for useSyncExternalStore).
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryKey, type QueryState } from '@tanstack/react-query';

export function useQueryStateSnapshot(queryKey: QueryKey): QueryState | undefined {
  const client = useQueryClient();
  const hash = JSON.stringify(queryKey);
  const subscribe = useCallback((onChange: () => void) => client.getQueryCache().subscribe(onChange), [client]);
  // The key is compared by value (hash), not by identity.
  const read = useCallback(() => client.getQueryState(queryKey), [client, hash]);
  return useSyncExternalStore(subscribe, read, read);
}

export type ListLoadState = 'loading' | 'error' | 'ready';

/**
 * loading: the first read has not answered yet (an empty list is not « empty »
 * until the server said so). error: the read failed and nothing was ever
 * loaded. Once rows exist, a failed refresh keeps showing them.
 */
export function listLoadState(state: QueryState | undefined, rowCount: number): ListLoadState {
  if (rowCount > 0) return 'ready';
  if (!state || state.status === 'pending') return 'loading';
  if (state.status === 'error' && state.dataUpdatedAt === 0) return 'error';
  return 'ready';
}
